import React, { useEffect, useRef } from "react";

import classNames from "classnames";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import CuePointsTableActions from "./CuePointsTableActions";
import CuepointsTableRow from "./CuePointsTableRow";
import {
  findInsertCuepointIndex,
  isValidTimecode,
  sortCuepoints,
} from "../../../utils/cuepoints";
import { timecodeToDuration } from "../../../utils/durations";
import { uniqBy } from "../../../utils/filter";

import type { JSX } from "react";
import type { UseFieldArrayReturn } from "react-hook-form";

import type {
  GetMediaResponse,
  MediaCuepoint,
  MediaCuepointAdded,
  MediaUpdated,
} from "../../../api/MediaAPI.types";
import type { CuepointsFormValues } from "../TabContentCuePoints";

interface CuePointsTableProps {
  item: GetMediaResponse;
  onItemUpdate: (media: MediaUpdated) => void;
  fieldArray: UseFieldArrayReturn<CuepointsFormValues, "cuepoints">;
  setFormValues: (formValues: CuepointsFormValues) => void;
  formValues: CuepointsFormValues;
}

function CuePointsTable({
  item,
  onItemUpdate,
  fieldArray,
  setFormValues,
  formValues,
}: CuePointsTableProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { trigger, watch, setFocus } = useFormContext<CuepointsFormValues>();
  const refScrollableContainer = useRef<HTMLDivElement>(null);

  const { fields, remove, append, replace, move } = fieldArray;

  const handleDeleteCuepoint = (index: number): void => {
    remove(index);
  };

  const { cuepoints } = formValues;

  const hasEmptyTimecode = cuepoints.some(
    (cuepoint) => cuepoint.timecode === "",
  );

  const hasScrollbar =
    refScrollableContainer.current &&
    refScrollableContainer.current?.scrollHeight >
      refScrollableContainer.current?.clientHeight;

  const handleAddCuepoint = (): void => {
    if (!hasEmptyTimecode) {
      append(
        {
          enabled: true,
          timecode: "",
        },
        {
          shouldFocus: true,
          focusName: "timecode",
        },
      );
      setTimeout(() => {
        trigger("cuepoints");
        setFocus(`cuepoints.${fields.length}.timecode`);
        if (refScrollableContainer.current) {
          refScrollableContainer.current?.scrollTo({
            top: refScrollableContainer.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 0);
    }
  };

  const handleDeleteAllCuepoints = (): void => {
    remove();
  };

  const handleBlurTimecode = (currentIndex: number): void => {
    const currentTimecode = cuepoints[currentIndex]?.timecode;

    if (currentTimecode && isValidTimecode(currentTimecode)) {
      const cuepointsWithoutCurrent = cuepoints.filter(
        (_, index) => index !== currentIndex,
      );

      const newIndex = findInsertCuepointIndex(
        cuepointsWithoutCurrent,
        currentTimecode,
      );

      const newIndewWithEmptyTimecode = hasEmptyTimecode
        ? newIndex - 1
        : newIndex;

      if (newIndewWithEmptyTimecode !== currentIndex) {
        move(currentIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    const formValuesListener = watch((data) => {
      const updatedCuepoints =
        data.cuepoints?.reduce(
          (acc: (MediaCuepoint | MediaCuepointAdded)[], cuepoint) => {
            if (
              cuepoint?.timecode &&
              isValidTimecode(cuepoint.timecode) &&
              cuepoint?.enabled !== undefined
            ) {
              acc.push({
                enabled: cuepoint.enabled,
                timecode: cuepoint.timecode,
                uuid: cuepoint?.uuid,
              });
            }

            return acc;
          },
          [],
        ) ?? [];

      trigger("cuepoints");

      onItemUpdate({ ...item, cuepoints: updatedCuepoints });
      if (data) {
        setFormValues(data as CuepointsFormValues);
      }
    });

    return () => {
      formValuesListener.unsubscribe();
    };
  }, [onItemUpdate, item, t, setFormValues, trigger, watch]);

  const mediaDurationMs = item.source_duration_ms;

  const computeCuepointsDataFile = (
    cuepointsDataFile: string[],
  ): MediaCuepointAdded[] => {
    const cuepointsFormated = cuepointsDataFile.map((cuepoint) => ({
      enabled: true,
      timecode: cuepoint,
    }));

    const filterCuepoints = cuepointsFormated.filter(
      (cuepoint) =>
        isValidTimecode(cuepoint.timecode) &&
        mediaDurationMs &&
        timecodeToDuration(cuepoint.timecode).millisecondsWithFrames <=
          mediaDurationMs,
    );

    const uniqCuepoints = uniqBy([...filterCuepoints, ...fields], "timecode");

    return uniqCuepoints;
  };

  const handleImportCuepoints = (cuepointsDataFile: File): void => {
    const reader = new FileReader();

    reader.readAsText(cuepointsDataFile);
    reader.onload = (): void => {
      try {
        const getCuepointsData = (): string[] => {
          const { result } = reader;
          const data =
            result && typeof result === "string" && JSON.parse(result);
          if (data && "cuepoints" in data && Array.isArray(data.cuepoints)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return data.cuepoints;
          }

          if (Array.isArray(data)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return data;
          }

          throw new Error("Invalid JSON file");
        };

        const cuepointsData = getCuepointsData();

        const cuepointsFormated = computeCuepointsDataFile(cuepointsData);

        const cuepointsSorted = sortCuepoints(cuepointsFormated);

        replace(cuepointsSorted);
      } catch (error) {
        console.error("Error during upload JSON file", error);
        toast.error(t("genericError", { ns: "error" }));
      }
    };
  };

  const addCuepointList = (cuepointList: MediaCuepointAdded[]): void => {
    const uniqCuepoints = uniqBy([...cuepointList, ...fields], "timecode");

    const cuepointsSorted = sortCuepoints(uniqCuepoints);

    replace(cuepointsSorted);
  };

  const tabHeaderClassName = classNames(
    "flex justify-between gap-[0.46875rem] border border-b-0 border-border bg-card px-6.25 py-3.75",
    hasScrollbar && "pr-10",
  );

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className={tabHeaderClassName}>
        <div className="flex basis-1/3">{t("status")}</div>
        <div className="flex basis-1/3 justify-center">{t("timecode")}</div>
        <div className="flex basis-1/3 justify-end">{t("action")}</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-6.25">
        <div
          ref={refScrollableContainer}
          className="border-border flex min-h-0 w-full flex-1 flex-col overflow-y-auto border pb-10"
        >
          <div className="flex flex-col">
            {fields.map((cuepoint, index) => (
              <CuepointsTableRow
                key={cuepoint.id}
                cuepoint={cuepoint}
                deleteCuepoint={handleDeleteCuepoint}
                index={index}
                mediaDurationMs={mediaDurationMs}
                onBlurTimecode={(): void => handleBlurTimecode(index)}
              />
            ))}
          </div>
        </div>
        <CuePointsTableActions
          addCuepoint={handleAddCuepoint}
          addCuepointList={addCuepointList}
          deleteAllCuepoints={handleDeleteAllCuepoints}
          importCuepoints={handleImportCuepoints}
          mediaDurationMs={mediaDurationMs}
        />
      </div>
    </div>
  );
}

export default CuePointsTable;
